import { useState, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  History,
  Loader2,
  Image as ImageIcon,
  Trash2,
  ChevronDown,
  SortAsc,
  SortDesc,
  ArrowDownAZ,
  Users,
  User,
  Filter,
  Check,
} from 'lucide-react';
import { Project } from '@/types';
import { getAvatarUrl } from '@/services/avatarService';

type SortOption = 'newest' | 'oldest' | 'name';
type FilterOption = 'all' | 'mine' | string; // string = member_id

interface DashboardViewProps {
  projects: Project[];
  isLoading?: boolean;
  onCreateNew: () => void;
  onLoadProject: (project: Project) => void;
  onDeleteProject: (projectId: string) => void;
  creatorMap?: Record<string, { name: string; email: string; avatarPath: string | null }>;
  currentUserId?: string;
  isAdmin?: boolean;
  teamMembers?: Array<{ id: string; name: string; email: string; avatarPath: string | null }>;
  projectAssignments?: Record<string, string[]>;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  projects,
  isLoading = false,
  onCreateNew,
  onLoadProject,
  onDeleteProject,
  creatorMap,
  currentUserId,
  isAdmin,
  teamMembers,
  projectAssignments,
}) => {
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const hasTeam = isAdmin && teamMembers && teamMembers.length > 0;

  // First-run nudge: pulse the primary CTA while the user has no projects yet.
  const hasNoProjects = !isLoading && projects.length === 0;

  // Project counts per filter option (creator + assigned members)
  const projectCounts = useMemo(() => {
    const counts: Record<string, number> = { all: projects.length, mine: 0 };
    for (const p of projects) {
      if (p.userId === currentUserId) counts.mine++;
      if (p.userId) counts[p.userId] = (counts[p.userId] || 0) + 1;
      // Count assigned members
      const assigned = projectAssignments?.[p.id] || [];
      for (const memberId of assigned) {
        if (memberId !== p.userId) {
          counts[memberId] = (counts[memberId] || 0) + 1;
        }
      }
    }
    return counts;
  }, [projects, currentUserId, projectAssignments]);

  // Filter then sort projects
  const sortedProjects = useMemo(() => {
    let filtered = [...projects];

    // Apply filter
    if (filterBy === 'mine') {
      filtered = filtered.filter(p => p.userId === currentUserId);
    } else if (filterBy !== 'all') {
      // Filter by creator OR assigned member
      filtered = filtered.filter(p =>
        p.userId === filterBy ||
        (projectAssignments?.[p.id]?.includes(filterBy) ?? false)
      );
    }

    // Apply sort
    switch (sortBy) {
      case 'newest':
        return filtered.sort((a, b) => b.createdAt - a.createdAt);
      case 'oldest':
        return filtered.sort((a, b) => a.createdAt - b.createdAt);
      case 'name':
        return filtered.sort((a, b) => a.name.localeCompare(b.name, 'de'));
      default:
        return filtered;
    }
  }, [projects, sortBy, filterBy, currentUserId, projectAssignments]);

  const sortOptions: { value: SortOption; label: string; icon: React.ReactNode }[] = [
    { value: 'newest', label: 'Neueste zuerst', icon: <SortDesc className="w-4 h-4" /> },
    { value: 'oldest', label: 'Älteste zuerst', icon: <SortAsc className="w-4 h-4" /> },
    { value: 'name', label: 'Name A-Z', icon: <ArrowDownAZ className="w-4 h-4" /> },
  ];

  const currentSortOption = sortOptions.find(o => o.value === sortBy);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    setIsDeleting(true);
    try {
      await onDeleteProject(deleteConfirm.id);
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Delete Confirmation Modal */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Projekt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Das Projekt <strong>"{deleteConfirm?.name}"</strong> und alle zugehörigen Bilder 
              werden unwiderruflich gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Löschen...
                </>
              ) : (
                'Endgültig löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          {/* Filter Dropdown (Admin with team only) */}
          {hasTeam && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  {filterBy !== 'all' && filterBy !== 'mine' && creatorMap?.[filterBy]?.avatarPath ? (
                    <img src={getAvatarUrl(creatorMap[filterBy].avatarPath!)} alt="" className="w-4 h-4 rounded-full object-cover" />
                  ) : (
                    <Filter className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">
                    {filterBy === 'all' ? 'Alle' : filterBy === 'mine' ? 'Meine' : (creatorMap?.[filterBy]?.name || creatorMap?.[filterBy]?.email || 'Mitglied')}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-xs text-muted-foreground font-medium">Ansicht</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={() => setFilterBy('all')}
                    className={filterBy === 'all' ? 'bg-accent' : ''}
                  >
                    <Users className="w-4 h-4 mr-2 text-muted-foreground" />
                    <span className="flex-1">Alle Projekte</span>
                    <span className="text-xs text-muted-foreground tabular-nums">{projectCounts.all}</span>
                    {filterBy === 'all' && <Check className="w-4 h-4 ml-2 text-primary" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setFilterBy('mine')}
                    className={filterBy === 'mine' ? 'bg-accent' : ''}
                  >
                    <User className="w-4 h-4 mr-2 text-muted-foreground" />
                    <span className="flex-1">Meine Projekte</span>
                    <span className="text-xs text-muted-foreground tabular-nums">{projectCounts.mine}</span>
                    {filterBy === 'mine' && <Check className="w-4 h-4 ml-2 text-primary" />}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground font-medium">Teammitglieder</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {teamMembers!.map((member) => (
                    <DropdownMenuItem
                      key={member.id}
                      onClick={() => setFilterBy(member.id)}
                      className={`${filterBy === member.id ? 'bg-accent' : ''}`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {member.avatarPath ? (
                          <img src={getAvatarUrl(member.avatarPath)} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] font-medium text-secondary-foreground">
                              {(member.name || member.email).slice(0, 2).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{member.name || member.email}</p>
                          {member.name && <p className="text-xs text-muted-foreground truncate">{member.email}</p>}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0">{projectCounts[member.id] || 0}</span>
                      {filterBy === member.id && <Check className="w-4 h-4 ml-2 text-primary flex-shrink-0" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Sort Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                {currentSortOption?.icon}
                <span className="hidden sm:inline">{currentSortOption?.label}</span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {sortOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setSortBy(option.value)}
                  className={sortBy === option.value ? 'bg-accent' : ''}
                >
                  <div className="flex items-center gap-2">
                    {option.icon}
                    {option.label}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Button
          onClick={onCreateNew}
          className={`flex items-center gap-2 rounded-[10px] border-2 border-[#FFD4BF] h-10 px-6${hasNoProjects ? ' attention-pulse' : ''}`}
        >
          Neues Projekt anlegen <Plus className="w-5 h-5" />
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : (
        <div className="grid gap-[13px] grid-cols-[repeat(auto-fill,minmax(226px,1fr))]">
          {sortedProjects.map((project) => (
            <div
              key={project.id}
              onClick={() => onLoadProject(project)}
              className="group relative flex flex-col gap-3 rounded-[20px] bg-card p-2 pb-3 shadow-[0px_4px_12.4px_0px_rgba(0,0,0,0.05)] transition-shadow hover:shadow-[0px_6px_18px_0px_rgba(0,0,0,0.08)] cursor-pointer"
            >
              {/* Delete Button */}
              <Button
                variant="secondary"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm(project);
                }}
                className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground transition-all duration-200 shadow-sm h-8 w-8"
                title="Projekt löschen"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>

              {/* Thumbnail — fixed landscape height, fluid width */}
              <div className="h-[200px] w-full overflow-hidden rounded-[12px] bg-muted">
                {project.thumbnailUrl ? (
                  <img
                    src={project.thumbnailUrl}
                    alt={project.name}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ImageIcon className="w-10 h-10 text-muted-foreground/40" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex flex-col gap-0.5 px-2">
                <p className="font-normal text-[14px] leading-[1.2] tracking-[-0.14px] text-foreground line-clamp-2">
                  {project.name}
                </p>
                <p className="font-normal text-[14px] leading-[1.4] tracking-[-0.14px] text-[#9CA3AF] truncate">
                  {project.address || 'Keine Adresse angegeben'}
                </p>
                <div className="mt-0.5 flex items-center gap-1 text-[12px] leading-[1.4] tracking-[-0.12px] text-[#9CA3AF]">
                  <History className="w-3 h-3" />
                  {new Date(project.createdAt).toLocaleDateString('de-DE')}
                </div>
                {creatorMap && currentUserId && project.userId && project.userId !== currentUserId && creatorMap[project.userId] && (
                  <div className="text-[11px] text-blue-600 dark:text-blue-400 flex items-center gap-1 mt-1">
                    {creatorMap[project.userId].avatarPath ? (
                      <img src={getAvatarUrl(creatorMap[project.userId].avatarPath!)} alt="" className="w-4 h-4 rounded-full object-cover" />
                    ) : (
                      <Users className="w-3 h-3" />
                    )}
                    {creatorMap[project.userId].name || creatorMap[project.userId].email}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* New Project Tile */}
          <button
            type="button"
            onClick={onCreateNew}
            className={`group relative flex min-h-[295px] flex-col items-center justify-center gap-1 rounded-[20px] border-2 border-dashed border-[#EBEBE6] bg-card transition-colors hover:border-primary/60${hasNoProjects ? ' attention-pulse' : ''}`}
          >
            <div className="mb-2 flex items-center justify-center rounded-full bg-[#FFF3EC] p-[10px] group-hover:bg-[#FFD4BF] transition-colors">
              <Plus className="h-9 w-9 text-primary" strokeWidth={2} />
            </div>
            <p className="font-medium text-[14px] tracking-[-0.14px] text-foreground">
              Neues Projekt anlegen
            </p>
            <p className="font-normal text-[12px] tracking-[-0.12px] text-muted-foreground">
              Jetzt mit neuem Staging starten
            </p>
          </button>
        </div>
      )}
    </div>
  );
};
